<script setup lang="ts">
import { useForm } from '@inertiajs/vue3'
import { computed, reactive, ref, watch } from 'vue'

import AddressForm from '@/components/AddressForm.vue'
import DobInput from '@/components/DobInput.vue'
import InputError from '@/components/InputError.vue'
import PhoneInput from '@/components/PhoneInput.vue'
import PortalPartnerCombobox from '@/components/PortalPartnerCombobox.vue'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { formatLabel } from '@/lib/utils'

interface Partner {
  id: number
  name: string
  locked_loan_type: string | null
}

interface Address {
  street_no: string
  street_name: string
  street_type: string
  unit_no: string
  city: string
  state: string
  postcode: string
  years_at_address: string
  months_at_address: string
  address_type: string
}

const props = defineProps<{
  submit_url: string
  showPartnerSelector?: boolean
  locked_loan_type?: string | null
  genders: string[]
  loan_terms: number[]
  purposes: Record<string, string[]>
  loan_types: string[]
  marital_statuses: string[]
  living_statuses: string[]
}>()

const defaultAddress = (type: string): Address => ({
  street_no: '',
  street_name: '',
  street_type: '',
  unit_no: '',
  city: '',
  state: '',
  postcode: '',
  years_at_address: '',
  months_at_address: '',
  address_type: type,
})

const currentAddress = reactive<Address>(defaultAddress('current'))
const previousAddress = reactive<Address>(defaultAddress('previous'))

const needsPreviousAddress = computed(() => {
  const years = parseInt(currentAddress.years_at_address)
  return !isNaN(years) && years < 3
})

const form = useForm({
  partner_id: '',
  customer: {
    first_name: '',
    last_name: '',
    middle_name: '',
    gender: '',
    date_of_birth: '',
    email: '',
    phone: '',
  },
  loan_type: '',
  purpose: '',
  amount: '',
  loan_term: '',
  marital_status: '',
  dependents: '0',
  monthly_income: '',
  annual_income: '',
  other_income: '',
  spouse_income: '',
  living_status: '',
  consent_attestation: false,
})

const availablePurposes = computed(() => {
  if (!form.loan_type) return []
  return props.purposes[form.loan_type] ?? []
})

function onLoanTypeChange() {
  form.purpose = ''
}

const selectedPartnerName = ref('')
const selectedPartnerLockedLoanType = ref<string | null>(null)

function onPartnerSelect(partner: Partner | null) {
  selectedPartnerLockedLoanType.value = partner?.locked_loan_type ?? null
}

// Loan type is locked to the partner's type whenever that type maps to a valid
// loan_type (every Partner::TYPES value except "broker" — see locked_loan_type_for
// in app/controllers/concerns/application_form_options.rb). On the org form this
// re-evaluates as the partner selection changes; on the partner form it's fixed.
const effectiveLockedLoanType = computed<string | null>(() => {
  if (props.showPartnerSelector) return selectedPartnerLockedLoanType.value
  return props.locked_loan_type ?? null
})

watch(
  effectiveLockedLoanType,
  (val) => {
    form.loan_type = val ?? ''
    form.purpose = ''
  },
  { immediate: true },
)

// LoanApplicationCreator returns unprefixed field errors (e.g. "customer.email",
// "partner_id", "loan_type") rather than an array-per-field shape.
const errors = computed(() => form.errors as Record<string, string | undefined>)

const consentError = computed(
  () =>
    errors.value['consent.privacy_policy'] ||
    errors.value['consent.credit_guide'] ||
    errors.value['consent.communication'] ||
    errors.value['consent.as_agent'],
)

function submit() {
  form
    .transform((data) => ({
      ...data,
      addresses: {
        current: currentAddress,
        previous: needsPreviousAddress.value ? previousAddress : undefined,
      },
    }))
    .post(props.submit_url)
}
</script>

<template>
  <form novalidate class="space-y-6" @submit.prevent="submit">
    <Card v-if="showPartnerSelector">
      <CardHeader>
        <CardTitle class="text-base">Partner</CardTitle>
      </CardHeader>
      <CardContent>
        <div class="grid gap-2">
          <Label for="partner_id">Partner <span class="text-destructive">*</span></Label>
          <PortalPartnerCombobox
            v-model="form.partner_id"
            v-model:partner-name="selectedPartnerName"
            placeholder="Select partner"
            @select="onPartnerSelect"
          />
          <InputError :messages="errors.partner_id" />
        </div>
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <CardTitle class="text-base">Customer</CardTitle>
      </CardHeader>
      <CardContent class="space-y-4">
        <div class="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
          <div class="grid gap-2">
            <Label for="customer_first_name">First Name <span class="text-destructive">*</span></Label>
            <Input
              id="customer_first_name"
              v-model="form.customer.first_name"
              autocomplete="given-name"
              placeholder="John"
            />
            <InputError :messages="errors['customer.first_name']" />
          </div>
          <div class="grid gap-2">
            <Label for="customer_last_name">Last Name <span class="text-destructive">*</span></Label>
            <Input
              id="customer_last_name"
              v-model="form.customer.last_name"
              autocomplete="family-name"
              placeholder="Smith"
            />
            <InputError :messages="errors['customer.last_name']" />
          </div>
        </div>
        <div class="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
          <div class="grid gap-2">
            <Label for="customer_middle_name">Middle Name</Label>
            <Input
              id="customer_middle_name"
              v-model="form.customer.middle_name"
              autocomplete="additional-name"
              placeholder="Optional"
            />
          </div>
          <div class="grid gap-2">
            <Label for="customer_gender">Gender <span class="text-destructive">*</span></Label>
            <Select v-model="form.customer.gender">
              <SelectTrigger id="customer_gender" class="w-full"
                ><SelectValue placeholder="Select gender"
              /></SelectTrigger>
              <SelectContent>
                <SelectItem v-for="g in genders" :key="g" :value="g">{{ g }}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div class="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
          <div class="grid gap-2">
            <Label for="customer_date_of_birth">Date of Birth <span class="text-destructive">*</span></Label>
            <DobInput
              id="customer_date_of_birth"
              v-model="form.customer.date_of_birth"
              :invalid="!!errors['customer.date_of_birth']"
            />
            <InputError :messages="errors['customer.date_of_birth']" />
          </div>
          <div class="grid gap-2">
            <Label for="customer_phone">Phone <span class="text-destructive">*</span></Label>
            <PhoneInput id="customer_phone" v-model="form.customer.phone" :invalid="!!errors['customer.phone']" />
            <InputError :messages="errors['customer.phone']" />
          </div>
        </div>
        <div class="grid gap-2">
          <Label for="customer_email">Email <span class="text-destructive">*</span></Label>
          <Input
            id="customer_email"
            v-model="form.customer.email"
            type="email"
            autocomplete="email"
            placeholder="john@example.com"
          />
          <InputError :messages="errors['customer.email']" />
        </div>
        <InputError :messages="errors.base" />
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <CardTitle class="text-base">Loan Information</CardTitle>
      </CardHeader>
      <CardContent class="space-y-4">
        <div class="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
          <div class="grid gap-2">
            <Label for="loan_type">Loan Type <span class="text-destructive">*</span></Label>
            <Select
              v-model="form.loan_type"
              :disabled="!!effectiveLockedLoanType"
              @update:model-value="onLoanTypeChange"
            >
              <SelectTrigger
                id="loan_type"
                :class="['w-full', errors.loan_type && 'border-destructive focus-visible:ring-destructive']"
              >
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="t in loan_types" :key="t" :value="t">{{ formatLabel(t) }}</SelectItem>
              </SelectContent>
            </Select>
            <p v-if="effectiveLockedLoanType" class="text-muted-foreground text-xs">Set by the partner's type</p>
            <InputError :messages="errors.loan_type" />
          </div>
          <div class="grid gap-2">
            <Label for="purpose">Purpose <span class="text-destructive">*</span></Label>
            <Select v-model="form.purpose" :disabled="!form.loan_type">
              <SelectTrigger
                id="purpose"
                :class="['w-full', errors.purpose && 'border-destructive focus-visible:ring-destructive']"
              >
                <SelectValue placeholder="Select purpose" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="p in availablePurposes" :key="p" :value="p">{{ formatLabel(p) }}</SelectItem>
              </SelectContent>
            </Select>
            <InputError :messages="errors.purpose" />
          </div>
        </div>
        <div class="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
          <div class="grid gap-2">
            <Label for="amount">Amount ($) <span class="text-destructive">*</span></Label>
            <Input
              id="amount"
              v-model="form.amount"
              type="number"
              min="2000"
              :max="form.loan_type === 'vehicle' ? 150000 : 72000"
              step="500"
              placeholder="5000"
            />
            <InputError :messages="errors.amount" />
          </div>
          <div class="grid gap-2">
            <Label for="loan_term">Loan Term (months)</Label>
            <Select v-model="form.loan_term">
              <SelectTrigger id="loan_term" class="w-full"><SelectValue placeholder="Select term" /></SelectTrigger>
              <SelectContent>
                <SelectItem v-for="t in loan_terms" :key="t" :value="String(t)">{{ t }} months</SelectItem>
              </SelectContent>
            </Select>
            <InputError :messages="errors.loan_term" />
          </div>
        </div>
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <CardTitle class="text-base">Financial Profile</CardTitle>
      </CardHeader>
      <CardContent class="space-y-4">
        <div class="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
          <div class="grid gap-2">
            <Label for="marital_status">Marital Status <span class="text-destructive">*</span></Label>
            <Select v-model="form.marital_status">
              <SelectTrigger
                id="marital_status"
                :class="['w-full', errors.marital_status && 'border-destructive focus-visible:ring-destructive']"
              >
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="s in marital_statuses" :key="s" :value="s">{{ formatLabel(s) }}</SelectItem>
              </SelectContent>
            </Select>
            <InputError :messages="errors.marital_status" />
          </div>
          <div class="grid gap-2">
            <Label for="dependents">Dependents <span class="text-destructive">*</span></Label>
            <Select v-model="form.dependents">
              <SelectTrigger id="dependents" class="w-full"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem v-for="n in 10" :key="n - 1" :value="String(n - 1)">{{ n - 1 }}</SelectItem>
              </SelectContent>
            </Select>
            <InputError :messages="errors.dependents" />
          </div>
        </div>
        <div class="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
          <div class="grid gap-2">
            <Label for="monthly_income">Monthly Income ($) <span class="text-destructive">*</span></Label>
            <Input id="monthly_income" v-model="form.monthly_income" type="number" min="0" />
            <InputError :messages="errors.monthly_income" />
          </div>
          <div class="grid gap-2">
            <Label for="annual_income">Annual Income ($) <span class="text-destructive">*</span></Label>
            <Input id="annual_income" v-model="form.annual_income" type="number" min="0" />
            <InputError :messages="errors.annual_income" />
          </div>
        </div>
        <div class="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
          <div class="grid gap-2">
            <Label for="other_income">Other Income ($)</Label>
            <Input id="other_income" v-model="form.other_income" type="number" min="0" placeholder="Optional" />
          </div>
          <div class="grid gap-2">
            <Label for="spouse_income">Spouse Income ($)</Label>
            <Input id="spouse_income" v-model="form.spouse_income" type="number" min="0" placeholder="Optional" />
          </div>
        </div>
        <div class="grid gap-2">
          <Label for="living_status">Living Status <span class="text-destructive">*</span></Label>
          <Select v-model="form.living_status">
            <SelectTrigger
              id="living_status"
              :class="['w-full', errors.living_status && 'border-destructive focus-visible:ring-destructive']"
            >
              <SelectValue placeholder="Select status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem v-for="s in living_statuses" :key="s" :value="s">{{ formatLabel(s) }}</SelectItem>
            </SelectContent>
          </Select>
          <InputError :messages="errors.living_status" />
        </div>
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <CardTitle class="text-base">Current Address</CardTitle>
      </CardHeader>
      <CardContent>
        <!-- The service doesn't distinguish which address a field-level error belongs to
             (see LoanApplicationCreator#keyed_errors_from), so both forms share the same
             unprefixed error set — matching the existing API channel's behavior. -->
        <AddressForm
          :model-value="currentAddress"
          :errors="errors"
          prefix="current_"
          show-duration
          @update:model-value="Object.assign(currentAddress, $event)"
        />
        <InputError :messages="errors['addresses.current']" />
      </CardContent>
    </Card>

    <Card v-if="needsPreviousAddress">
      <CardHeader>
        <CardTitle class="text-base">Previous Address</CardTitle>
        <p class="text-muted-foreground mt-1 text-sm">Required — less than 3 years at current address</p>
      </CardHeader>
      <CardContent>
        <AddressForm
          :model-value="previousAddress"
          :errors="errors"
          prefix="previous_"
          show-duration
          @update:model-value="Object.assign(previousAddress, $event)"
        />
        <InputError :messages="errors['addresses.previous']" />
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <CardTitle class="text-base">Consent</CardTitle>
      </CardHeader>
      <CardContent>
        <div class="flex gap-3">
          <Checkbox
            id="consent_attestation"
            :modelValue="form.consent_attestation"
            class="mt-0.5 shrink-0"
            @update:modelValue="form.consent_attestation = !!$event"
          />
          <label for="consent_attestation" class="cursor-pointer text-sm leading-relaxed">
            I confirm the customer has provided privacy policy, credit guide, communications, and agent consent to
            submit this application on their behalf.
          </label>
        </div>
        <InputError :messages="consentError" />
      </CardContent>
    </Card>

    <Button type="submit" class="w-full" :disabled="form.processing"> Create Application </Button>
  </form>
</template>
